import axios from 'axios';
import { get as _get, invoke as _invoke, sortBy as _sortBy } from 'lodash';
import moment from 'moment';

import { type FormResponses, type Pass, type SanitisedPass } from '@sine/sine-core-api-types';

import otaForceDownload from '../../components/otaForceDownload';
import formatDate from '../../helpers/formatDate';
import serializeQueryParams from '../../helpers/serializeQueryParams';
import { PASSES_FETCH_LIMIT } from '../../pass/pass.constants';
import { SineHeader } from '../../shared/types';
import rxEventHub, { RxEvent } from '../events/rx';
import type {
  FetchActivePassesQuery,
  FetchInactivePassesQuery,
  GetUpdatedPassesQuery,
  GetUserPassesQuery,
  InactivePassesResponse,
  PassInfo,
  PrefillFormQuery,
} from './pass.types';

function truncateSearchTerm(searchTerm?: string) {
  return searchTerm ? searchTerm.substring(0, 100) : searchTerm;
}

const passService = {
  // API: passes.service.getUserPasses
  async getMyPasses({ status, limit = PASSES_FETCH_LIMIT, createdBefore }: GetUserPassesQuery) {
    const params: GetUserPassesQuery = {
      status,
      limit,
    };

    if (createdBefore) {
      params.createdBefore = moment(createdBefore).unix();
    }

    const { data } = await axios.get<{
      passes: Array<SanitisedPass<Pass>>;
      maxStartDate: number | null;
    }>(`${window.env.REACT_APP_SINE_CORE_API_URI}/passes/mine?${serializeQueryParams(params)}`);

    return data;
  },

  // API: pass.service.getUpdatedPasses
  async getRecentPasses({ status, siteId, searchTerm, updatedAfter }: GetUpdatedPassesQuery) {
    const params: GetUpdatedPassesQuery = {
      status,
      siteId,
      searchTerm: truncateSearchTerm(searchTerm),
      updatedAfter: moment(updatedAfter).unix(),
    };

    const { data } = await axios.get<Array<SanitisedPass<Pass>>>(
      `${window.env.REACT_APP_SINE_CORE_API_URI}/pass/recent?${serializeQueryParams(params)}`,
    );

    return data;
  },

  async getInfo(passId: string, teamId: string | undefined) {
    const { data } = await axios.get<PassInfo>(`${window.env.REACT_APP_SINE_CORE_API_URI}/pass/info/${passId}`, {
      headers: {
        ...(teamId && { [SineHeader.X_SINE_TEAM_ID]: teamId }),
      },
    });

    return data;
  },

  async getActivePasses() {
    // not sure why no params are passes in this function, use any here temporarily)
    const data = await this.fetchPassQuery('fetch', { status: ['active', 'pending'] } as any);

    return _sortBy(
      data?.map((pass) => ({
        id: _get(pass, 'id', ''),
        name: `${_get(pass, 'firstName', '')} ${_get(pass, 'lastName', '')}`.trim(),
        email: _get(pass, 'email', ''),
        mobile: _get(pass, 'mobile', ''),
        issuedDate: formatDate(pass.startDate),
        site: _get(pass, 'siteDetails.name', ''),
        company: _get(pass, 'companyDetails.name', ''),
        host: `${_get(pass, 'hostDetails.firstName', '')} ${_get(pass, 'hostDetails.lastName', '')}`.trim(),
        zone: _get(pass, 'zoneDetails.name', ''),
      })),
      (d) => _invoke(d, 'name.toLowerCase'),
    );
  },

  async prefillForm(siteId: string, params: PrefillFormQuery) {
    const { data } = await axios.post<FormResponses>(
      `${window.env.REACT_APP_SINE_CORE_API_URI}/pass/form-prefill/user/${siteId}`,
      params,
    );

    return data;
  },

  async emailPasses(statuses: Array<string> = []) {
    await axios.post(`${window.env.REACT_APP_SINE_CORE_API_URI}/pass/email-list`, {
      status: statuses,
    });
  },

  async updateNotes(passId: string, { adminNotes, hostNotes }: { adminNotes?: string; hostNotes?: string }) {
    const { data: pass } = await axios.post<SanitisedPass<Pass>>(
      `${window.env.REACT_APP_SINE_CORE_API_URI}/pass/notes/${passId}`,
      {
        adminNotes,
        hostNotes,
      },
    );

    rxEventHub.broadcast(RxEvent.PASS_UPDATED, { pass });

    return pass;
  },

  async acceptPass(passId: string, token?: string) {
    const { data: pass } = await axios.post<SanitisedPass<Pass>>(
      `${window.env.REACT_APP_SINE_CORE_API_URI}/pass/status/accept/${passId}`,
      {
        token,
      },
    );

    rxEventHub.broadcast(RxEvent.PASS_UPDATED, { pass });

    return pass;
  },

  async rejectPass(passId: string, token?: string) {
    const { data: pass } = await axios.post<SanitisedPass<Pass>>(
      `${window.env.REACT_APP_SINE_CORE_API_URI}/pass/status/reject/${passId}`,
      {
        token,
      },
    );

    rxEventHub.broadcast(RxEvent.PASS_UPDATED, { pass });

    return pass;
  },

  async deletePass(passId: string) {
    await axios.delete(`${window.env.REACT_APP_SINE_CORE_API_URI}/pass/${passId}`);
  },

  async deleteFacialData(passId: string) {
    await axios.delete(`${window.env.REACT_APP_SINE_CORE_API_URI}/pass/facial-data/${passId}`);
  },

  async getDocument(documentType: string, fileType: string) {
    switch (fileType) {
      case 'image': {
        return otaForceDownload.downloadAWS(
          `${window.env.REACT_APP_SINE_CORE_API_URI}/user/document/type/${documentType}/file?shouldReturnLink=true`,
          'download.jpg',
        );
      }

      case 'pdf': {
        return otaForceDownload.downloadAWS(
          `${window.env.REACT_APP_SINE_CORE_API_URI}/user/document/type/${documentType}/file?shouldReturnLink=true`,
          'download.pdf',
        );
      }
    }
  },

  async previewDocument(passId: string, documentType: string, fileType: string) {
    // Pipe the image as a data blob
    if (fileType === 'image') {
      const { data } = await axios.get<Record<string, any>>(
        `${window.env.REACT_APP_SINE_CORE_API_URI}/pass/document/${passId}/type/${documentType}/ajax`,
        {
          responseType: 'blob',
        },
      );

      const objectUrl = (window as any).URL || window.webkitURL;

      return objectUrl.createObjectURL(data);
    }
  },

  async downloadDocument(documentType: string, fileType: string, passId: string) {
    if (fileType === 'image') {
      return otaForceDownload.download(
        `${window.env.REACT_APP_SINE_CORE_API_URI}/pass/document/${passId}/type/${documentType}/ajax`,
        'download.jpg',
      );
    } else {
      return otaForceDownload.download(
        `${window.env.REACT_APP_SINE_CORE_API_URI}/pass/document/${passId}/type/${documentType}/ajax`,
        'download.pdf',
      );
    }
  },

  async printRemote(passId: string) {
    await axios.post(`${window.env.REACT_APP_SINE_CORE_API_URI}/pass/print/${passId}`);
  },

  async pollUpdatedPasses({
    lastFetchTime,
    status,
    siteId,
    searchTerm,
  }: {
    siteId: string | undefined;
    searchTerm: string | undefined;
    status: string | undefined;
    lastFetchTime: Date | number;
  }) {
    const params = {
      updatedAfter: moment(lastFetchTime).unix(),
      status,
      siteId,
      searchTerm: searchTerm ? searchTerm.substring(0, 100) : searchTerm,
    };

    const { data } = await axios.get<{ passes: Array<Pass>; activeCount: number }>(
      `${window.env.REACT_APP_SINE_CORE_API_URI}/pass/recent?${serializeQueryParams(params)}`,
    );

    return data;
  },

  async fetchPassQuery(
    apiMethodName: string,
    { status, siteId, searchTerm, updatedAfter, createdBefore, limit = 1000, autoExpired }: FetchActivePassesQuery,
  ) {
    const params: FetchActivePassesQuery = {
      status,
      limit,
      siteId,
      searchTerm: truncateSearchTerm(searchTerm),
      cacheBuster: moment().unix(), // we need this to prevent aggressive caching in IE
      autoExpired,
    };

    if (createdBefore) {
      params.createdBefore = moment(createdBefore).unix();
    }

    if (updatedAfter) {
      params.updatedAfter = moment(updatedAfter).unix();
    }

    const { data } = await axios.get<Array<SanitisedPass<Pass>> | undefined>(
      `${window.env.REACT_APP_SINE_CORE_API_URI}/pass/${apiMethodName}?${serializeQueryParams(params)}`,
    );

    return data;
  },

  async fetchInactivePassQuery({
    status,
    siteId,
    searchTerm,
    limit = 101,
    startedBefore,
    autoExpired,
  }: FetchInactivePassesQuery) {
    const params: FetchInactivePassesQuery = {
      status,
      siteId,
      searchTerm: truncateSearchTerm(searchTerm),
      limit,
      cacheBuster: moment().unix(),
      autoExpired,
    };

    if (startedBefore) {
      params.startedBefore = moment(startedBefore).unix();
    }

    const { data } = await axios.get<InactivePassesResponse>(
      `${window.env.REACT_APP_SINE_CORE_API_URI}/pass/fetch-inactive?${serializeQueryParams(params)}`,
    );

    return data;
  },

  async fetchSinglePass(passId: string) {
    const { data } = await axios.get<SanitisedPass<Pass>>(
      `${window.env.REACT_APP_SINE_CORE_API_URI}/pass/fetch/${passId}`,
    );

    return data;
  },

  /**
   * Export an Apple wallet pass for pass.
   */
  async exportAppleWalletPass(passId: string, teamId?: string) {
    const { data } = await axios.get<ArrayBufferLike>(
      `${window.env.REACT_APP_SINE_CORE_API_URI}/pass/${passId}/export/apple`,
      {
        responseType: 'arraybuffer',
        ...(teamId && { headers: { [SineHeader.X_SINE_TEAM_ID]: teamId } }),
      },
    );

    const dataArray = new Uint8Array(data);
    const passFileBlob = new Blob([dataArray], { type: 'application/vnd.apple.pkpass' });

    return passFileBlob;
  },

  /**
   * Export a Google wallet pass for pass.
   */
  async exportGoogleWalletPass(passId: string, teamId?: string) {
    const { data } = await axios.get<string>(`${window.env.REACT_APP_SINE_CORE_API_URI}/pass/${passId}/export/google`, {
      ...(teamId && { headers: { [SineHeader.X_SINE_TEAM_ID]: teamId } }),
    });

    return data;
  },
};

export default passService;
